import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    Kbd,
    SimpleGrid,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';

import { getTradeSide } from '@/cip/Optimizer/OptimizationResults/TradeExecution/tradeExecutionUtils';

import { type EnrichedOptimizerTrade } from '../../../_openapi/portfolio-optimizer';
import { getColumnLabel } from '../../../utils/tableUtils';
import { AssetClassTag } from '../../../widgets/AssetClassTag';
import { DateCell } from '../../../widgets/Table/DateCell';
import { DateTimeCell } from '../../../widgets/Table/DateTimeCell';
import { MoneyCell } from '../../../widgets/Table/MoneyCell';
import { NumberCell } from '../../../widgets/Table/NumberCell';
import { TextCell } from '../../../widgets/Table/TextCell';
import { TradeSideTag } from '../../../widgets/TradeSideTag';

export const TradeDetailsDrawer = ({
    isOpen,
    onClose,
    trade,
}: {
    isOpen: boolean;
    onClose: () => void;
    trade?: EnrichedOptimizerTrade;
}) => {
    const lines = useMemo(() => {
        if (!trade) {
            return [];
        }

        return [
            { label: getColumnLabel('pId'), value: <TextCell value={trade.pId} /> },
            { label: getColumnLabel('portfolioGuid'), value: <TextCell value={trade.portfolioGuid} /> },
            { label: getColumnLabel('aC'), value: <TextCell value={trade.aC} /> },
            { label: getColumnLabel('brokerId'), value: <TextCell value={trade.brokerId} /> },
            { label: getColumnLabel('sId'), value: <TextCell value={trade.sId} /> },
            { label: getColumnLabel('symbol'), value: <TextCell value={trade.asset?.symbol} /> },
            { label: getColumnLabel('description'), value: <TextCell value={trade.asset?.description} /> },
            {
                label: getColumnLabel('assetClass'),
                value: <AssetClassTag assetClass={trade.asset?.assetClass} />,
            },
            { label: getColumnLabel('assetType'), value: <TextCell value={trade.asset?.assetType} /> },
            { label: getColumnLabel('priceMultiplier'), value: <NumberCell value={trade.asset?.priceMultiplier} /> },
            {
                label: getColumnLabel('contractMultiplier'),
                value: <NumberCell value={trade.asset?.contractMultiplier} fractionDigits={0} />,
            },
            { label: getColumnLabel('orderPrice'), value: <MoneyCell value={trade.orderPrice} /> },
            { label: getColumnLabel('priceSource'), value: <TextCell value={trade.priceSource} /> },
            { label: getColumnLabel('priceTimestamp'), value: <DateTimeCell dateString={trade.priceTimestamp} /> },
            { label: getColumnLabel('side'), value: <TradeSideTag side={getTradeSide(trade)} /> },
            { label: getColumnLabel('quantity'), value: <NumberCell value={trade.tradeQuantity} /> },
            { label: getColumnLabel('tradeValue'), value: <MoneyCell value={trade.tradeValue} /> },
            { label: getColumnLabel('tradeDate'), value: <DateCell dateString={trade.tradeDate} /> },
            { label: getColumnLabel('tradeId'), value: <TextCell value={trade.tradeId} /> },
            { label: getColumnLabel('optimizationId'), value: <TextCell value={trade.optimizationId} /> },
            { label: getColumnLabel('allocationType'), value: <TextCell value={trade.allocationType} /> },
            { label: getColumnLabel('createdAt'), value: <DateCell dateString={trade.createdAt} /> },
        ];
    }, [trade]);

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />

                <DrawerHeader borderBottomWidth={1}>
                    <Text flexShrink={0}>Trade details</Text>
                </DrawerHeader>

                <DrawerBody>
                    <SimpleGrid columnGap={10} gridTemplateColumns="max-content max-content">
                        {lines.map(({ label, value }) => (
                            <React.Fragment key={label}>
                                <Text>{label}</Text>
                                <Flex justifyContent="flex-start" alignItems="center">
                                    {value}
                                </Flex>
                            </React.Fragment>
                        ))}
                    </SimpleGrid>
                </DrawerBody>

                <DrawerFooter gap={4} justifyContent="space-between">
                    <Box>
                        <Alert status="info" fontSize="xs">
                            <AlertIcon boxSize={4} />
                            <Text>
                                You can close this panel by hitting <Kbd>Esc</Kbd> or clicking the backdrop
                            </Text>
                        </Alert>
                    </Box>
                    <Button variant="outline" onClick={onClose} flexShrink={0}>
                        Close
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
};

export const useTradeDetailsDrawerControls = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [trade, setTrade] = useState<EnrichedOptimizerTrade>();

    const viewTrade = useCallback(
        (t: EnrichedOptimizerTrade) => {
            setTrade(t);
            onOpen();
        },
        [onOpen],
    );

    return {
        viewTrade,
        trade,
        isOpen,
        onClose,
    };
};
